/**
 * GAELLE — Frontend Enhancement Layer
 * Version: 1.0.0
 *
 * Designed for:
 * - Shopify
 * - Gecko / T4S theme
 * - Hebrew RTL + English LTR
 *
 * This file intentionally avoids replacing Gecko's core functionality.
 * It provides GAELLE-specific UI behavior and progressive enhancement.
 */

(function () {
  'use strict';

  /* =========================================================
     GAELLE GLOBAL OBJECT
  ========================================================= */

  window.Gaelle = window.Gaelle || {};

  var Gaelle = window.Gaelle;

  Gaelle.version = '1.0.0';


  /* =========================================================
     UTILITIES
  ========================================================= */

  Gaelle.$ = function (selector, context) {
    return (context || document).querySelector(selector);
  };

  Gaelle.$$ = function (selector, context) {
    return Array.prototype.slice.call(
      (context || document).querySelectorAll(selector)
    );
  };

  Gaelle.isRTL = function () {
    return document.documentElement.getAttribute('dir') === 'rtl';
  };

  Gaelle.debounce = function (callback, delay) {
    var timer;

    return function () {
      var context = this;
      var args = arguments;

      clearTimeout(timer);

      timer = setTimeout(function () {
        callback.apply(context, args);
      }, delay || 150);
    };
  };

  Gaelle.dispatch = function (name, detail) {
    document.dispatchEvent(
      new CustomEvent('gaelle:' + name, {
        bubbles: true,
        detail: detail || {}
      })
    );
  };


  /* =========================================================
     MOBILE MENU
  ========================================================= */

  Gaelle.initMobileMenu = function () {
    var menuButtons = Gaelle.$$(
      '[data-gaelle-menu-toggle], .gaelle-menu-toggle'
    );

    var closeButtons = Gaelle.$$(
      '[data-gaelle-menu-close], .gaelle-menu-close'
    );

    var menu = Gaelle.$(
      '[data-gaelle-mobile-menu], .gaelle-mobile-menu'
    );

    var overlay = Gaelle.$(
      '[data-gaelle-menu-overlay], .gaelle-menu-overlay'
    );

    if (!menu) {
      return;
    }

    function openMenu() {
      document.documentElement.classList.add('gaelle-menu-open');
      document.body.classList.add('gaelle-menu-open');

      menu.classList.add('is-open');
      menu.setAttribute('aria-hidden', 'false');

      if (overlay) {
        overlay.classList.add('is-visible');
      }

      menuButtons.forEach(function (button) {
        button.setAttribute('aria-expanded', 'true');
      });

      Gaelle.dispatch('menu:open');
    }

    function closeMenu() {
      document.documentElement.classList.remove('gaelle-menu-open');
      document.body.classList.remove('gaelle-menu-open');

      menu.classList.remove('is-open');
      menu.setAttribute('aria-hidden', 'true');

      if (overlay) {
        overlay.classList.remove('is-visible');
      }

      menuButtons.forEach(function (button) {
        button.setAttribute('aria-expanded', 'false');
      });

      Gaelle.dispatch('menu:close');
    }

    menuButtons.forEach(function (button) {
      button.addEventListener('click', function (event) {
        event.preventDefault();

        if (menu.classList.contains('is-open')) {
          closeMenu();
        } else {
          openMenu();
        }
      });
    });

    closeButtons.forEach(function (button) {
      button.addEventListener('click', function (event) {
        event.preventDefault();
        closeMenu();
      });
    });

    if (overlay) {
      overlay.addEventListener('click', closeMenu);
    }

    document.addEventListener('keydown', function (event) {
      if (event.key === 'Escape' && menu.classList.contains('is-open')) {
        closeMenu();
      }
    });

    /*
     * Close mobile menu after clicking a normal navigation link.
     */

    Gaelle.$$('.gaelle-mobile-menu a').forEach(function (link) {
      link.addEventListener('click', function () {
        closeMenu();
      });
    });
  };


  /* =========================================================
     MOBILE SUBMENUS / ACCORDIONS
  ========================================================= */

  Gaelle.initMobileSubmenus = function () {
    var toggles = Gaelle.$$(
      '[data-gaelle-submenu-toggle]'
    );

    toggles.forEach(function (toggle) {
      toggle.addEventListener('click', function (event) {
        event.preventDefault();

        var targetId = toggle.getAttribute('aria-controls');

        if (!targetId) {
          return;
        }

        var submenu = document.getElementById(targetId);

        if (!submenu) {
          return;
        }

        var isOpen = toggle.getAttribute('aria-expanded') === 'true';

        toggle.setAttribute(
          'aria-expanded',
          isOpen ? 'false' : 'true'
        );

        submenu.classList.toggle(
          'is-open',
          !isOpen
        );
      });
    });
  };


  /* =========================================================
     HEADER SCROLL BEHAVIOR
  ========================================================= */

  Gaelle.initHeader = function () {
    var header = Gaelle.$(
      '[data-gaelle-header], .gaelle-header'
    );

    if (!header) {
      return;
    }

    var scrollThreshold = 20;

    function updateHeader() {
      if (window.scrollY > scrollThreshold) {
        header.classList.add('is-scrolled');
        document.documentElement.classList.add(
          'gaelle-header-scrolled'
        );
      } else {
        header.classList.remove('is-scrolled');
        document.documentElement.classList.remove(
          'gaelle-header-scrolled'
        );
      }
    }

    updateHeader();

    window.addEventListener(
      'scroll',
      Gaelle.debounce(updateHeader, 10),
      {
        passive: true
      }
    );
  };


  /* =========================================================
     SEARCH OVERLAY
  ========================================================= */

  Gaelle.initSearch = function () {
    var searchToggles = Gaelle.$$(
      '[data-gaelle-search-toggle], .gaelle-search-toggle'
    );

    var searchOverlay = Gaelle.$(
      '[data-gaelle-search], .gaelle-search-overlay'
    );

    var closeButtons = Gaelle.$$(
      '[data-gaelle-search-close], .gaelle-search-close'
    );

    if (!searchOverlay || !searchToggles.length) {
      return;
    }

    function openSearch() {
      searchOverlay.classList.add('is-open');

      searchOverlay.setAttribute(
        'aria-hidden',
        'false'
      );

      document.body.classList.add(
        'gaelle-search-open'
      );

      var input = searchOverlay.querySelector(
        'input[type="search"], input[name="q"]'
      );

      if (input) {
        setTimeout(function () {
          input.focus();
        }, 100);
      }

      Gaelle.dispatch('search:open');
    }

    function closeSearch() {
      searchOverlay.classList.remove('is-open');

      searchOverlay.setAttribute(
        'aria-hidden',
        'true'
      );

      document.body.classList.remove(
        'gaelle-search-open'
      );

      Gaelle.dispatch('search:close');
    }

    searchToggles.forEach(function (toggle) {
      toggle.addEventListener('click', function (event) {
        event.preventDefault();
        openSearch();
      });
    });

    closeButtons.forEach(function (button) {
      button.addEventListener('click', function (event) {
        event.preventDefault();
        closeSearch();
      });
    });

    document.addEventListener('keydown', function (event) {
      if (
        event.key === 'Escape' &&
        searchOverlay.classList.contains('is-open')
      ) {
        closeSearch();
      }
    });
  };


  /* =========================================================
     ACCORDIONS
  ========================================================= */

  Gaelle.initAccordions = function () {
    var accordions = Gaelle.$$(
      '[data-gaelle-accordion]'
    );

    accordions.forEach(function (accordion) {
      var triggers = accordion.querySelectorAll(
        '[data-gaelle-accordion-trigger]'
      );

      triggers.forEach(function (trigger) {
        trigger.addEventListener('click', function () {
          var item = trigger.closest(
            '[data-gaelle-accordion-item]'
          );

          if (!item) {
            return;
          }

          var content = item.querySelector(
            '[data-gaelle-accordion-content]'
          );

          if (!content) {
            return;
          }

          var isOpen = item.classList.contains(
            'is-open'
          );

          /*
           * Close other accordion items when
           * data-gaelle-accordion-single is present.
           */

          if (
            accordion.hasAttribute(
              'data-gaelle-accordion-single'
            )
          ) {
            accordion
              .querySelectorAll(
                '[data-gaelle-accordion-item].is-open'
              )
              .forEach(function (openItem) {
                if (openItem !== item) {
                  openItem.classList.remove(
                    'is-open'
                  );

                  var openTrigger =
                    openItem.querySelector(
                      '[data-gaelle-accordion-trigger]'
                    );

                  if (openTrigger) {
                    openTrigger.setAttribute(
                      'aria-expanded',
                      'false'
                    );
                  }
                }
              });
          }

          item.classList.toggle(
            'is-open',
            !isOpen
          );

          trigger.setAttribute(
            'aria-expanded',
            isOpen ? 'false' : 'true'
          );
        });
      });
    });
  };


  /* =========================================================
     PRODUCT GALLERY
  ========================================================= */

  Gaelle.initProductGallery = function () {
    var galleries = Gaelle.$$(
      '[data-gaelle-product-gallery]'
    );

    galleries.forEach(function (gallery) {
      var thumbnails = gallery.querySelectorAll(
        '[data-gaelle-gallery-thumbnail]'
      );

      var images = gallery.querySelectorAll(
        '[data-gaelle-gallery-image]'
      );

      if (!thumbnails.length || !images.length) {
        return;
      }

      thumbnails.forEach(function (thumbnail) {
        thumbnail.addEventListener('click', function () {
          var targetId =
            thumbnail.getAttribute(
              'data-gaelle-gallery-thumbnail'
            );

          if (!targetId) {
            return;
          }

          images.forEach(function (image) {
            image.classList.remove(
              'is-active'
            );
          });

          thumbnails.forEach(function (item) {
            item.classList.remove(
              'is-active'
            );

            item.setAttribute(
              'aria-selected',
              'false'
            );
          });

          var targetImage = gallery.querySelector(
            '[data-gaelle-gallery-image="' +
              targetId +
              '"]'
          );

          if (targetImage) {
            targetImage.classList.add(
              'is-active'
            );
          }

          thumbnail.classList.add(
            'is-active'
          );

          thumbnail.setAttribute(
            'aria-selected',
            'true'
          );
        });
      });
    });
  };


  /* =========================================================
     PRODUCT QUANTITY
  ========================================================= */

  Gaelle.initQuantitySelectors = function () {
    var selectors = Gaelle.$$(
      '[data-gaelle-quantity]'
    );

    selectors.forEach(function (selector) {
      var input = selector.querySelector(
        'input[type="number"]'
      );

      var minus = selector.querySelector(
        '[data-gaelle-quantity-minus]'
      );

      var plus = selector.querySelector(
        '[data-gaelle-quantity-plus]'
      );

      if (!input) {
        return;
      }

      function getMin() {
        return parseInt(
          input.getAttribute('min') || '1',
          10
        );
      }

      function getMax() {
        var max = input.getAttribute('max');

        return max
          ? parseInt(max, 10)
          : Infinity;
      }

      function setValue(value) {
        value = parseInt(value, 10);

        if (isNaN(value)) {
          value = getMin();
        }

        value = Math.max(
          getMin(),
          Math.min(value, getMax())
        );

        input.value = value;

        input.dispatchEvent(
          new Event('change', {
            bubbles: true
          })
        );
      }

      if (minus) {
        minus.addEventListener(
          'click',
          function () {
            setValue(
              parseInt(input.value || '1', 10) - 1
            );
          }
        );
      }

      if (plus) {
        plus.addEventListener(
          'click',
          function () {
            setValue(
              parseInt(input.value || '1', 10) + 1
            );
          }
        );
      }
    });
  };


  /* =========================================================
     COLLECTION FILTER UI
  ========================================================= */

  Gaelle.initCollectionFilters = function () {
    var filterToggle = Gaelle.$(
      '[data-gaelle-filter-toggle]'
    );

    var filterPanel = Gaelle.$(
      '[data-gaelle-filter-panel]'
    );

    var filterClose = Gaelle.$(
      '[data-gaelle-filter-close]'
    );

    var filterOverlay = Gaelle.$(
      '[data-gaelle-filter-overlay]'
    );

    if (!filterToggle || !filterPanel) {
      return;
    }

    function openFilters() {
      filterPanel.classList.add(
        'is-open'
      );

      document.body.classList.add(
        'gaelle-filters-open'
      );

      filterPanel.setAttribute(
        'aria-hidden',
        'false'
      );
    }

    function closeFilters() {
      filterPanel.classList.remove(
        'is-open'
      );

      document.body.classList.remove(
        'gaelle-filters-open'
      );

      filterPanel.setAttribute(
        'aria-hidden',
        'true'
      );
    }

    filterToggle.addEventListener(
      'click',
      function (event) {
        event.preventDefault();
        openFilters();
      }
    );

    if (filterClose) {
      filterClose.addEventListener(
        'click',
        function (event) {
          event.preventDefault();
          closeFilters();
        }
      );
    }

    if (filterOverlay) {
      filterOverlay.addEventListener(
        'click',
        closeFilters
      );
    }
  };


  /* =========================================================
     PRODUCT CARD HOVER
     ========================================================= */

  Gaelle.initProductCards = function () {
    var cards = Gaelle.$$(
      '[data-gaelle-product-card]'
    );

    cards.forEach(function (card) {
      var secondImage = card.querySelector(
        '[data-gaelle-product-image-secondary]'
      );

      if (!secondImage) {
        return;
      }

      card.addEventListener(
        'mouseenter',
        function () {
          card.classList.add(
            'is-hovered'
          );
        }
      );

      card.addEventListener(
        'mouseleave',
        function () {
          card.classList.remove(
            'is-hovered'
          );
        }
      );
    });
  };


  /* =========================================================
     SCROLL REVEAL
  ========================================================= */

  Gaelle.initScrollReveal = function () {
    var elements = Gaelle.$$(
      '[data-gaelle-reveal]'
    );

    if (!elements.length) {
      return;
    }

    /*
     * Respect reduced-motion preferences.
     */

    if (
      window.matchMedia &&
      window.matchMedia(
        '(prefers-reduced-motion: reduce)'
      ).matches
    ) {
      elements.forEach(function (element) {
        element.classList.add(
          'is-visible'
        );
      });

      return;
    }

    /*
     * Fallback for browsers without IntersectionObserver.
     */

    if (!('IntersectionObserver' in window)) {
      elements.forEach(function (element) {
        element.classList.add(
          'is-visible'
        );
      });

      return;
    }

    var observer =
      new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (entry.isIntersecting) {
              entry.target.classList.add(
                'is-visible'
              );

              observer.unobserve(
                entry.target
              );
            }
          });
        },
        {
          threshold: 0.12,
          rootMargin: '0px 0px -40px 0px'
        }
      );

    elements.forEach(function (element) {
      observer.observe(element);
    });
  };


  /* =========================================================
     SMOOTH ANCHOR LINKS
  ========================================================= */

  Gaelle.initSmoothLinks = function () {
    var links = Gaelle.$$(
      'a[href^="#"]:not([href="#"])'
    );

    links.forEach(function (link) {
      link.addEventListener(
        'click',
        function (event) {
          var href =
            link.getAttribute('href');

          if (!href) {
            return;
          }

          var target =
            document.querySelector(href);

          if (!target) {
            return;
          }

          event.preventDefault();

          target.scrollIntoView({
            behavior: 'smooth',
            block: 'start'
          });
        }
      );
    });
  };


  /* =========================================================
     NEWSLETTER FORM FEEDBACK
  ========================================================= */

  Gaelle.initNewsletter = function () {
    var forms = Gaelle.$$(
      '[data-gaelle-newsletter-form]'
    );

    forms.forEach(function (form) {
      form.addEventListener(
        'submit',
        function () {
          form.classList.add(
            'is-submitting'
          );
        }
      );
    });
  };


  /* =========================================================
     CART DRAWER BRIDGE
  =========================================================
     
     Gecko owns the actual cart logic.
     This function only listens for GAELLE events
     and provides CSS state hooks.
  ========================================================= */

  Gaelle.initCartBridge = function () {
    document.addEventListener(
      'gaelle:cart:open',
      function () {
        document.body.classList.add(
          'gaelle-cart-open'
        );
      }
    );

    document.addEventListener(
      'gaelle:cart:close',
      function () {
        document.body.classList.remove(
          'gaelle-cart-open'
        );
      }
    );

    /*
     * Shopify theme/app integrations can dispatch:
     *
     * document.dispatchEvent(
     *   new CustomEvent('gaelle:cart:open')
     * );
     *
     * without GAELLE trying to replace Gecko's cart.
     */
  };


  /* =========================================================
     SHOPIFY SECTION EVENTS
     ========================================================= */

  Gaelle.initShopifyEvents = function () {
    /*
     * Shopify Theme Editor:
     * Re-initialize GAELLE functionality
     * when a section is loaded dynamically.
     */

    document.addEventListener(
      'shopify:section:load',
      function (event) {
        var section = event.target;

        if (!section) {
          return;
        }

        Gaelle.initAccordions();
        Gaelle.initProductGallery();
        Gaelle.initQuantitySelectors();
        Gaelle.initProductCards();
        Gaelle.initScrollReveal();

        Gaelle.dispatch(
          'section:loaded',
          {
            sectionId:
              event.detail &&
              event.detail.sectionId
          }
        );
      }
    );
  };


  /* =========================================================
     RESIZE HANDLING
  ========================================================= */

  Gaelle.initResize = function () {
    var previousWidth =
      window.innerWidth;

    window.addEventListener(
      'resize',
      Gaelle.debounce(
        function () {
          var currentWidth =
            window.innerWidth;

          /*
           * Only dispatch when crossing
           * a mobile/desktop breakpoint.
           */

          var wasMobile =
            previousWidth < 768;

          var isMobile =
            currentWidth < 768;

          if (wasMobile !== isMobile) {
            Gaelle.dispatch(
              'breakpoint:change',
              {
                isMobile: isMobile
              }
            );
          }

          previousWidth = currentWidth;
        },
        200
      )
    );
  };


  /* =========================================================
     INITIALIZATION
  ========================================================= */

  Gaelle.init = function () {
    /*
     * Core GAELLE UI
     */

    Gaelle.initHeader();
    Gaelle.initMobileMenu();
    Gaelle.initMobileSubmenus();
    Gaelle.initSearch();
    Gaelle.initAccordions();

    /*
     * Commerce UI enhancements
     */

    Gaelle.initProductGallery();
    Gaelle.initQuantitySelectors();
    Gaelle.initCollectionFilters();
    Gaelle.initProductCards();

    /*
     * Visual enhancements
     */

    Gaelle.initScrollReveal();
    Gaelle.initSmoothLinks();

    /*
     * Forms / integrations
     */

    Gaelle.initNewsletter();
    Gaelle.initCartBridge();

    /*
     * Shopify / responsive behavior
     */

    Gaelle.initShopifyEvents();
    Gaelle.initResize();

    /*
     * Global event
     */

    Gaelle.dispatch(
      'initialized',
      {
        version: Gaelle.version,
        rtl: Gaelle.isRTL()
      }
    );
  };


  /* =========================================================
     START
  ========================================================= */

  if (
    document.readyState === 'loading'
  ) {
    document.addEventListener(
      'DOMContentLoaded',
      Gaelle.init
    );
  } else {
    Gaelle.init();
  }

})();